import React, { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema, generateBreadcrumbSchema } from '../../utils/seo';

const DuplicateTitleServicePage: React.FC = () => {
  const [selectedScenario, setSelectedScenario] = useState<string>('lost');
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';

  useSEO({
    title: 'Nevada Duplicate Title Service Las Vegas - Fast Replacement Titles',
    description: 'Professional duplicate title services in Las Vegas Nevada. Fast, secure replacement for lost, stolen, or damaged Nevada vehicle titles. Same-day processing available. Speedy DMV. Call 702.979.7202.',
    keywords: 'duplicate title service Nevada, lost title Las Vegas, stolen title replacement Las Vegas, damaged vehicle title Nevada, duplicate title application Nevada',
    canonical: `${siteUrl}/duplicate-title-service`,
    ogImage: `${siteUrl}/img/speedy-dmv-services.jpg`,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateLocalBusinessSchema(),
        generateServiceSchema(
          'Nevada Duplicate Title Service',
          'Professional duplicate title services for lost, stolen, or damaged Nevada vehicle titles in Las Vegas.',
          '100'
        ),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Services', url: '/services' },
          { name: 'Duplicate Title Service', url: '/duplicate-title-service' }
        ])
      ]
    }
  });

  const scenarios = {
    lost: {
      title: 'Lost Title',
      description: 'Original title has been misplaced or lost',
      requirements: [
        'Valid Nevada driver license or ID',
        'Current vehicle registration',
        'Nevada Evidence of Insurance',
        'Completed Application for Duplicate Title (VP 012)',
        'Payment for duplicate title fee'
      ]
    },
    stolen: {
      title: 'Stolen Title',
      description: 'Title was stolen along with other documents',
      requirements: [
        'Police report documenting theft',
        'Valid Nevada driver license or ID',
        'Current vehicle registration',
        'Nevada Evidence of Insurance',
        'Completed Application for Duplicate Title (VP 012)',
        'Affidavit of theft (if required)'
      ]
    },
    damaged: {
      title: 'Damaged Title',
      description: 'Title is damaged and no longer legible',
      requirements: [
        'Damaged original title (if available)',
        'Valid Nevada driver license or ID',
        'Current vehicle registration',
        'Nevada Evidence of Insurance',
        'Completed Application for Duplicate Title (VP 012)'
      ]
    }
  };

  const services = [
    {
      icon: 'ri-file-text-line',
      title: 'Duplicate Nevada Titles',
      description: 'Replace lost, stolen, or damaged Nevada vehicle titles with official duplicates.'
    },
    {
      icon: 'ri-time-line',
      title: 'Expedited Processing',
      description: 'Fast processing to get your duplicate title as quickly as possible.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Fraud Protection',
      description: 'Secure verification process to prevent fraudulent duplicate title requests.'
    },
    {
      icon: 'ri-customer-service-line',
      title: 'Expert Assistance',
      description: 'Professional guidance through the entire duplicate title application process.'
    }
  ];

  const processSteps = [
    {
      step: 1,
      title: 'Verify Eligibility',
      description: 'Confirm the vehicle was last titled in Nevada and verify your ownership status.',
      time: '5 minutes'
    },
    {
      step: 2,
      title: 'Complete Application',
      description: 'Fill out the Nevada Application for Duplicate Title with accurate information.',
      time: '10 minutes'
    },
    {
      step: 3,
      title: 'Document Review',
      description: 'We review your identification, registration, and supporting documents.',
      time: '10 minutes'
    },
    {
      step: 4,
      title: 'Submit & Process',
      description: 'Submit application to Nevada DMV and process payment for duplicate title.',
      time: '15 minutes'
    }
  ];

  const toggleExpanded = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-32">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-indigo-900 via-indigo-800 to-blue-900 text-white py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Nevada Duplicate Title Services
              </h1>
              <p className="text-xl mb-8 max-w-3xl mx-auto">
                Fast, secure duplicate title services for lost, stolen, or damaged Nevada vehicle titles. 
                Professional assistance to get your replacement title quickly and legally.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:702-979-7202" 
                   className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                  Call Now: (702) 979-7202
                </a>
                <a href="/appointment" 
                   className="bg-white text-indigo-900 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                  Schedule Appointment
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Complete Duplicate Title Solutions
              </h2>
              <p className="text-lg text-gray-600">
                Professional duplicate title services for all Nevada vehicle title replacement needs
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((service, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-md text-center">
                  <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${service.icon} text-2xl text-indigo-600`}></i>
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{service.title}</h3>
                  <p className="text-gray-600 text-sm">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Scenarios */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Duplicate Title Situations
              </h2>
              <p className="text-lg text-gray-600">
                Choose your situation to see specific requirements for your duplicate title request
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              {/* Scenario Selector */}
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                {Object.entries(scenarios).map(([key, scenario]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedScenario(key)}
                    className={`px-6 py-3 rounded-lg font-medium transition-colors whitespace-nowrap ${
                      selectedScenario === key 
                        ? 'bg-indigo-600 text-white' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {scenario.title}
                  </button>
                ))}
              </div>

              {/* Scenario Details */}
              <div className="bg-white rounded-lg shadow-md p-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                  {scenarios[selectedScenario as keyof typeof scenarios].title} Requirements
                </h3>
                <p className="text-gray-600 mb-6">
                  {scenarios[selectedScenario as keyof typeof scenarios].description}
                </p>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-4">Required Documents:</h4>
                  <div className="space-y-3">
                    {scenarios[selectedScenario as keyof typeof scenarios].requirements.map((req, index) => (
                      <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                        <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-sm text-indigo-600"></i>
                        </div>
                        <span className="text-gray-800">{req}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Important Information */}
        <section className="py-16 bg-yellow-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="bg-white rounded-lg shadow-md p-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-alert-line text-2xl text-red-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Title Backlog Notice
                  </h3>
                  <div className="space-y-4 text-gray-700">
                    <p className="font-medium text-red-600">
                      The Nevada DMV currently has a significant backlog in processing vehicle titles.
                    </p>
                    <div>
                      <p className="mb-3">
                        <strong>What this means for you:</strong>
                      </p>
                      <ul className="list-disc list-inside space-y-2 ml-4">
                        <li>Duplicate title processing may take 6-8 weeks longer than normal</li>
                        <li>We recommend patience with duplicate title requests</li>
                        <li>Financial institutions are being asked to work with customers during this period</li>
                        <li>We'll provide status updates throughout the process</li>
                      </ul>
                    </div>
                    <p className="text-sm text-gray-600 italic">
                      We appreciate your patience as Nevada DMV works to resolve this backlog situation.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Process Steps */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Duplicate Title Process
              </h2>
              <p className="text-lg text-gray-600">
                Our systematic approach ensures your duplicate title application is processed correctly
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="space-y-8">
                {processSteps.map((item, index) => (
                  <div key={index} className="flex items-start space-x-6">
                    <div className="w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
                        <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                        <span className="text-sm font-medium text-indigo-600 bg-indigo-100 px-3 py-1 rounded-full">
                          {item.time}
                        </span>
                      </div>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Special Situations */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Special Situations
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <i className="ri-bank-line text-xl text-blue-600"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">Vehicle with Lien</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  If your vehicle has an active loan or lease, the lienholder is responsible for ordering 
                  the duplicate title after the lien is satisfied.
                </p>
                <div className="space-y-2">
                  <p className="text-sm text-gray-700"><strong>Options:</strong></p>
                  <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 ml-4">
                    <li>Contact your lender directly</li>
                    <li>Request lien release and apply for duplicate</li>
                    <li>We can help coordinate with lienholders</li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-map-line text-xl text-green-600"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">Out-of-State Titles</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  If your vehicle was last titled in another state, you must obtain a duplicate 
                  from that state's DMV office.
                </p>
                <div className="space-y-2">
                  <p className="text-sm text-gray-700"><strong>We can help with:</strong></p>
                  <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 ml-4">
                    <li>Identifying the correct state DMV</li>
                    <li>Providing contact information</li>
                    <li>Explaining their requirements</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Duplicate Title Questions
              </h2>
            </div>

            <div className="space-y-4">
              {[
                {
                  question: "How long does it take to receive a duplicate title?",
                  answer: "Due to current processing backlogs, duplicate titles are taking 6-8 weeks to be mailed. We'll provide tracking information once your application is submitted."
                },
                {
                  question: "Can I sell my vehicle without the title?",
                  answer: "No, a valid title is required to sell a vehicle in Nevada. You must obtain a duplicate title before completing any sale transaction."
                },
                {
                  question: "What if I need to prove ownership immediately?",
                  answer: "We can provide a temporary receipt showing your duplicate title application is in process. This may help with insurance or other immediate needs."
                },
                {
                  question: "How much does a duplicate title cost?",
                  answer: "Nevada charges different fees based on where the title is mailed. We'll explain all applicable fees during your appointment."
                },
                {
                  question: "What if someone else has my title?",
                  answer: "If you suspect fraudulent use of your title, contact us immediately. We can help you file the appropriate reports and secure your vehicle ownership."
                }
              ].map((faq, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md">
                  <button
                    className="w-full px-6 py-4 text-left flex items-center justify-between focus:outline-none"
                    onClick={() => toggleExpanded(`faq-${index}`)}
                  >
                    <span className="font-semibold text-gray-900">{faq.question}</span>
                    <i className={`ri-arrow-${expandedSection === `faq-${index}` ? 'up' : 'down'}-s-line text-xl text-gray-500`}></i>
                  </button>
                  {expandedSection === `faq-${index}` && (
                    <div className="px-6 pb-4">
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-16 bg-indigo-900 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Need a Duplicate Title?
            </h2>
            <p className="text-xl mb-8">
              📞 Appointments Required – Please Call Ahead
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:702-979-7202" 
                 className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                Call (702) 979-7202
              </a>
              <a href="/required-documents" 
                 className="bg-white text-indigo-900 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                Document Checklist
              </a>
            </div>
            <div className="mt-6 text-gray-300">
              <p>Speedy DMV Services - 4525 S Sandhill Rd, Las Vegas, NV</p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default DuplicateTitleServicePage;
